export type PostHogIdentityUser = {
  id: string;
  name?: string | null;
  email?: string | null;
  is_guest?: boolean;
  created_at?: string;
};

export type PostHogLike = {
  get_distinct_id: () => string;
  identify: (user_id: string, properties?: Record<string, unknown>) => void;
  alias: (alias: string) => void;
};

const HOOKDECK_USER_ID_PREFIX = 'usr_';
const POSTHOG_IDENTITY_SYNCED_SESSION_KEY = 'hookdeck_posthog_identity_synced';

/** Set after pre-redirect identity sync so the next page load skips a duplicate alias. */
export const markPostHogIdentitySyncedForNextPage = (user_id: string): void => {
  try {
    sessionStorage.setItem(POSTHOG_IDENTITY_SYNCED_SESSION_KEY, user_id);
  } catch {
    // sessionStorage unavailable (private mode, SSR)
  }
};

/** One-time consume: true when auth redirect already synced this user on the prior page. */
export const consumePostHogIdentitySyncedForPage = (user_id: string): boolean => {
  try {
    const synced_user_id = sessionStorage.getItem(POSTHOG_IDENTITY_SYNCED_SESSION_KEY);
    if (synced_user_id === user_id) {
      sessionStorage.removeItem(POSTHOG_IDENTITY_SYNCED_SESSION_KEY);
      return true;
    }
  } catch {
    // sessionStorage unavailable
  }
  return false;
};

/** PostHog anonymous ids are UUIDs; Hookdeck user ids use the usr_ prefix. */
export const isAnonymousDistinctId = (distinct_id: string): boolean =>
  !distinct_id.startsWith(HOOKDECK_USER_ID_PREFIX);

const shouldLinkDistinctIdToUser = (current_distinct_id: string, user_id: string): boolean => {
  if (current_distinct_id === user_id) {
    return false;
  }

  // Only link an anonymous browser id (e.g. from a sign-in/sign-up page) onto the
  // user. Never alias one identified Hookdeck user id (usr_) onto another — PostHog
  // would merge two distinct real people (or log a "Refused to merge an already
  // identified user" warning). A stale identified id from a prior, un-reset session
  // must not be aliased onto the current user.
  return isAnonymousDistinctId(current_distinct_id);
};

/**
 * Guest users stay capture-only (distinct_id only). Identified users get posthog.identify.
 * When the browser distinct_id differs from the known user id, alias links prior anonymous
 * events before continuing under the stable id.
 */
export const syncPostHogIdentity = (
  posthog: PostHogLike,
  user: PostHogIdentityUser | null | undefined,
  extra_properties: Record<string, unknown> = {}
): void => {
  if (!posthog || !user) {
    return;
  }

  const current_distinct_id = posthog.get_distinct_id();
  const should_link_distinct_id = shouldLinkDistinctIdToUser(current_distinct_id, user.id);

  if (user.is_guest) {
    // Guests stay capture-only: do NOT alias or identify. Under
    // person_profiles: 'identified_only' both operations create a person profile,
    // which is exactly what we want to avoid for guests. The anonymous events
    // captured now are linked to the real user at upgrade time, when is_guest
    // flips to false and the identify path below runs.
    return;
  }

  if (should_link_distinct_id) {
    posthog.alias(user.id);
  }

  posthog.identify(user.id, {
    name: user.name,
    email: user.email,
    ...extra_properties,
  });
};

/**
 * Fetch the authenticated user and sync PostHog identity while the current page still owns
 * the anonymous distinct_id (e.g. immediately after sign-in, before a hard redirect).
 */
export const syncPostHogIdentityAfterSession = async (
  posthog: PostHogLike | undefined,
  fetch_user: () => Promise<PostHogIdentityUser | null | undefined>,
  extra_properties: Record<string, unknown> = {}
): Promise<void> => {
  if (!posthog) {
    return;
  }

  try {
    const user = await fetch_user();
    if (!user) {
      return;
    }
    syncPostHogIdentity(posthog, user, extra_properties);
    markPostHogIdentitySyncedForNextPage(user.id);
  } catch {
    // Never block auth redirects; UserContext may still sync on the next page.
  }
};
